/*
  HELION — brand behaviour on top of Dawn.
  One file, no libraries. IntersectionObserver + requestAnimationFrame only.
  Every feature must also work, fully static, with prefers-reduced-motion.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Sticky product bar
     ------------------------------------------------------------------------ */
  class HelionProductBar extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.onNavClick = this.onNavClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
    }

    connectedCallback() {
      this.toggle = this.querySelector('.helion-bar__toggle');
      this.nav = this.querySelector('.helion-bar__nav');
      this.buy = this.querySelector('.helion-bar__buy');
      this.pinnedByEditor = false;
      this.pastTrigger = false;

      // Buy scrolls to the purchase section when this page has one.
      const buyTarget = this.dataset.buyAnchor ? document.getElementById(this.dataset.buyAnchor) : null;
      if (buyTarget && this.buy) this.buy.setAttribute('href', `#${buyTarget.id}`);

      this.toggle?.addEventListener('click', this.onToggle);
      this.nav?.addEventListener('click', this.onNavClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);

      this.observeTrigger();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }

    findTrigger() {
      const byId = this.dataset.showAfter ? document.getElementById(this.dataset.showAfter) : null;
      if (byId) return byId;

      // Fallback: the first section in <main> that isn't this bar.
      const ownSection = this.closest('.shopify-section');
      const sections = document.querySelectorAll('#MainContent > .shopify-section');
      return Array.from(sections).find((section) => section !== ownSection) || null;
    }

    observeTrigger() {
      this.observer?.disconnect();
      const trigger = this.findTrigger();
      if (!trigger) {
        this.pastTrigger = true;
        this.render();
        return;
      }

      const barHeight = this.offsetHeight || 52;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          // Past = the trigger has scrolled up and out from under the bar.
          this.pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.render();
        },
        { rootMargin: `-${barHeight}px 0px 0px 0px` }
      );
      this.observer.observe(trigger);
    }

    render() {
      const visible = this.pastTrigger || this.pinnedByEditor;
      this.classList.toggle('is-visible', visible);
      if (!visible) this.setOpen(false);
    }

    setOpen(open) {
      if (!this.toggle) return;
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
    }

    onToggle() {
      this.setOpen(this.toggle.getAttribute('aria-expanded') !== 'true');
    }

    onNavClick(event) {
      if (event.target.closest('a')) this.setOpen(false);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
      this.setOpen(false);
      this.toggle.focus();
    }

    // Theme editor: keep the bar in view while its section is selected.
    onSectionSelect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      this.pinnedByEditor = true;
      this.render();
    }

    onSectionDeselect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      this.pinnedByEditor = false;
      this.render();
    }
  }

  if (!customElements.get('helion-product-bar')) {
    customElements.define('helion-product-bar', HelionProductBar);
  }

  /* ------------------------------------------------------------------------
     Hero video: plays silent while on screen, pauses off screen,
     never plays with reduced motion. The button is the WCAG pause control.
     ------------------------------------------------------------------------ */
  class HelionHeroMedia extends HTMLElement {
    constructor() {
      super();
      this.update = this.update.bind(this);
      this.onToggle = this.onToggle.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('.helion-hero__toggle');
      if (!this.video) return;

      this.video.muted = true;
      if (navigator.connection?.saveData === true) this.classList.add('is-still');
      this.userPaused = false;
      this.inView = false;
      try {
        this.sources = JSON.parse(this.video.dataset.sources || '[]');
      } catch (error) {
        this.sources = [];
      }
      if (document.readyState !== 'complete') {
        window.addEventListener('load', this.update, { once: true });
      }
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('error', () => {
        // The smaller file failed: step up to the full-size one.
        const index = this.sources.findIndex((source) => source.src === this.video.getAttribute('src'));
        const next = this.sources[index + 1];
        if (index === -1 || !next) return;
        this.video.src = next.src;
        this.update();
      });
      this.toggle?.addEventListener('click', this.onToggle);
      reducedMotion.addEventListener('change', this.update);

      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.update();
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      reducedMotion.removeEventListener('change', this.update);
    }

    onToggle() {
      this.userPaused = !this.userPaused;
      this.update();
    }

    // Smallest file that still looks sharp here: phones get 720p, large screens 1080p.
    pickSource() {
      const needed = this.clientWidth * Math.min(window.devicePixelRatio || 1, 2) * 0.7;
      return this.sources.find((source) => source.width >= needed) || this.sources[this.sources.length - 1];
    }

    update() {
      // Data Saver on: the poster stays, the video never downloads.
      const saveData = navigator.connection?.saveData === true;
      const shouldPlay =
        this.inView &&
        !this.userPaused &&
        !reducedMotion.matches &&
        !saveData &&
        document.readyState === 'complete';
      if (shouldPlay) {
        if (!this.video.getAttribute('src')) {
          const source = this.pickSource();
          if (source) this.video.src = source.src;
        }
        if (this.video.getAttribute('src')) this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }

      this.classList.toggle('is-paused', this.userPaused);
      if (this.toggle) {
        const label = this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
        this.toggle.querySelector('.visually-hidden').textContent = label;
      }
    }
  }

  if (!customElements.get('helion-hero-media')) {
    customElements.define('helion-hero-media', HelionHeroMedia);
  }

  /* ------------------------------------------------------------------------
     Thickness scene — the one orchestrated moment.
     The stage pins; scroll position (not time) drives every beat, so
     scrolling back plays it in reverse. Opacity and transform only.
     ------------------------------------------------------------------------ */
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const segment = (p, start, end) => clamp((p - start) / (end - start), 0, 1);
  const smooth = (t) => t * t * (3 - 2 * t);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  // Scroll map (0 = scene pins, 1 = scene releases)
  // Short crossfades with a hold on each view, so it reads as a turn.
  const BEATS = {
    faceToSide: [0.1, 0.18],
    sideToEdge: [0.26, 0.34],
    sharpEdge: [0.4, 0.46], // invisible swap to the sharp edge from the comparison render
    brick: [0.46, 0.74],
    line: [0.74, 0.86],
  };

  class HelionThickness extends HTMLElement {
    constructor() {
      super();
      this.setup = this.setup.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('.helion-thickness');
      this.stage = this.querySelector('.helion-thickness__stage');
      this.group = this.querySelector('.helion-thickness__group');
      this.line = this.querySelector('.helion-thickness__line');
      this.layers = {};
      this.querySelectorAll('[data-layer]').forEach((layer) => {
        this.layers[layer.dataset.layer] = layer;
      });

      reducedMotion.addEventListener('change', this.setup);
      this.setup();
    }

    disconnectedCallback() {
      reducedMotion.removeEventListener('change', this.setup);
      this.teardown();
    }

    setup() {
      this.teardown();
      if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

      const drift = parseFloat(getComputedStyle(this.section).getPropertyValue('--drift'));
      this.drift = Number.isFinite(drift) ? drift : 0;
      this.section.classList.add('is-scrubbing');
      Object.values(this.layers).forEach((layer) => {
        layer.loading = 'eager';
      });

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            window.addEventListener('scroll', this.requestUpdate, { passive: true });
            window.addEventListener('resize', this.requestUpdate);
            this.requestUpdate();
          } else {
            window.removeEventListener('scroll', this.requestUpdate);
            window.removeEventListener('resize', this.requestUpdate);
          }
        },
        { rootMargin: '100px 0px' }
      );
      this.observer.observe(this);
      this.requestUpdate();
    }

    teardown() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.requestUpdate);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.section?.classList.remove('is-scrubbing');
      [this.group, this.line, ...Object.values(this.layers || {})].forEach((element) => {
        if (element) element.removeAttribute('style');
      });
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.render();
      });
    }

    render() {
      const rect = this.getBoundingClientRect();
      const distance = rect.height - this.stage.offsetHeight;
      const p = distance > 0 ? clamp(-rect.top / distance, 0, 1) : 1;
      const { side, edge, edge_hd: sharpEdge, brick } = this.layers;

      if (side) side.style.opacity = smooth(segment(p, ...BEATS.faceToSide));
      if (edge) edge.style.opacity = smooth(segment(p, ...BEATS.sideToEdge));
      if (sharpEdge) sharpEdge.style.opacity = smooth(segment(p, ...BEATS.sharpEdge));

      const slide = easeOut(segment(p, ...BEATS.brick));
      if (brick) brick.style.transform = `translate3d(${((1 - slide) * window.innerWidth).toFixed(1)}px, 0, 0)`;

      const drift = easeInOut(segment(p, ...BEATS.brick)) * this.drift;
      this.group.style.transform = `translate3d(${drift.toFixed(3)}%, 0, 0)`;

      this.line.style.opacity = smooth(segment(p, ...BEATS.line));
    }
  }

  if (!customElements.get('helion-thickness')) {
    customElements.define('helion-thickness', HelionThickness);
  }

  /* ------------------------------------------------------------------------
     In the box: as the section scrolls in, the box turns from its HELION
     spine (84°) to face the viewer square-on, lands at 80% of the scroll and
     holds on the front for the last 20%; scrolling up turns it back. Not
     pinned, so the thickness scene stays the one bold moment.
     Turntable frames 36 → 8 only: the back of the box must never appear.
     Frames load when the section is about a screen away, coarse to fine; the
     first (spine) frame stands in as the poster, so the canvas takes over
     without a jump. Reduced motion: the front render only, no frames.
     ------------------------------------------------------------------------ */
  const BOX_LANDS = 0.8; // share of the scroll range spent turning; the rest holds the front

  class HelionBoxTurn extends HTMLElement {
    constructor() {
      super();
      this.setup = this.setup.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.canvas = this.querySelector('.helion-box__canvas');
      this.poster = this.querySelector('.helion-box__poster');
      this.front = this.poster && { src: this.poster.getAttribute('src'), srcset: this.poster.getAttribute('srcset') };
      this.urls = (this.dataset.frames || '').split(',').filter(Boolean);
      this.frames = [];
      reducedMotion.addEventListener('change', this.setup);
      this.setup();
    }

    disconnectedCallback() {
      reducedMotion.removeEventListener('change', this.setup);
      this.teardown();
    }

    setup() {
      this.teardown();
      if (reducedMotion.matches || !this.canvas || this.urls.length < 2 || !('IntersectionObserver' in window)) return;

      // The turn starts on the spine, so the spine frame stands in for the front render
      if (this.poster) {
        this.poster.removeAttribute('srcset');
        this.poster.src = this.urls[0];
      }

      this.context = this.canvas.getContext('2d');
      this.position = 0; // eased frame position, 0 … frames - 1
      this.target = 0;
      this.drawn = null;

      this.loader = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.loader.disconnect();
          this.load();
        },
        { rootMargin: '100% 0px' }
      );
      this.loader.observe(this);

      // Follow the scroll only while the box is on screen
      this.watcher = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.onScroll();
        } else {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      });
      this.watcher.observe(this);
    }

    teardown() {
      this.loader?.disconnect();
      this.watcher?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.classList.remove('is-turning');
      if (this.poster && this.front) {
        this.poster.src = this.front.src;
        if (this.front.srcset) this.poster.setAttribute('srcset', this.front.srcset);
      }
    }

    // First and last frame, then the middle, then each half: the turn works early
    load() {
      const count = this.urls.length;
      const order = [0, count - 1];
      let gaps = [[0, count - 1]];
      while (gaps.length) {
        const next = [];
        gaps.forEach(([from, to]) => {
          if (to - from < 2) return;
          const middle = Math.round((from + to) / 2);
          order.push(middle);
          next.push([from, middle], [middle, to]);
        });
        gaps = next;
      }

      order.forEach((index) => {
        if (this.frames[index]) return;
        const image = new Image();
        image.decoding = 'async';
        image.onload = () => {
          this.frames[index] = image;
          this.drawn = null;
          if (this.isConnected && !reducedMotion.matches) {
            this.classList.add('is-turning');
            this.requestTick();
          }
        };
        image.src = this.urls[index];
      });
    }

    // 0 as the box comes up into view, 1 once its centre passes the upper third.
    // The turn eases to a stop on the front at BOX_LANDS, then holds there.
    onScroll() {
      const rect = this.getBoundingClientRect();
      const view = window.innerHeight;
      const start = view * 0.95 + rect.height / 2;
      const end = view * 0.35;
      const progress = clamp((start - (rect.top + rect.height / 2)) / (start - end), 0, 1);
      this.target = easeInOut(clamp(progress / BOX_LANDS, 0, 1)) * (this.urls.length - 1);
      this.requestTick();
    }

    requestTick() {
      if (!this.frame) this.frame = requestAnimationFrame(this.tick);
    }

    // Ease toward the scroll position, so a fast flick still turns smoothly
    tick() {
      this.frame = null;
      const gap = this.target - this.position;
      this.position = Math.abs(gap) < 0.01 ? this.target : this.position + gap * 0.18;
      this.draw();
      if (this.position !== this.target) this.requestTick();
    }

    nearest(index) {
      for (let step = 0; step < this.urls.length; step++) {
        if (this.frames[index - step]) return this.frames[index - step];
        if (this.frames[index + step]) return this.frames[index + step];
      }
      return null;
    }

    // Blend each frame into the next (3° apart), so the turn has no steps
    draw() {
      const base = Math.floor(this.position);
      const mix = this.position - base;
      const from = this.frames[base] || this.nearest(base);
      if (!from) return;
      const to = mix > 0.01 && this.frames[base] ? this.frames[base + 1] : null;
      if (this.drawn && this.drawn.from === from && this.drawn.to === to && Math.abs(this.drawn.mix - mix) < 0.01) return;
      this.drawn = { from, to, mix };

      const { width, height } = this.canvas;
      this.context.clearRect(0, 0, width, height);
      this.context.globalAlpha = 1;
      this.context.drawImage(from, 0, 0, width, height);
      if (to) {
        this.context.globalAlpha = mix;
        this.context.drawImage(to, 0, 0, width, height);
        this.context.globalAlpha = 1;
      }
    }
  }

  if (!customElements.get('helion-box-turn')) {
    customElements.define('helion-box-turn', HelionBoxTurn);
  }

  /* ------------------------------------------------------------------------
     Every angle: named buttons jump to a view; swiping or trackpad
     scrolling updates which button is current. Arrow keys move between views.
     ------------------------------------------------------------------------ */
  class HelionAngles extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.helion-angles__track');
      this.slides = Array.from(this.querySelectorAll('.helion-angles__slide'));
      this.buttons = Array.from(this.querySelectorAll('.helion-angles__button'));
      if (!this.track || this.slides.length === 0) return;

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.go(index));
        button.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(index) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.setCurrent(target);
    }

    setCurrent(index) {
      this.buttons.forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.setCurrent(Math.round(this.track.scrollLeft / this.track.clientWidth));
      });
    }

    onKeydown(event, index) {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
      if (!step) return;
      event.preventDefault();
      const next = Math.max(0, Math.min(this.buttons.length - 1, index + step));
      this.go(next);
      this.buttons[next].focus();
    }

    // Theme editor: show the view whose block is selected
    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index !== -1) this.go(index);
    }
  }

  if (!customElements.get('helion-angles')) {
    customElements.define('helion-angles', HelionAngles);
  }

  /* ------------------------------------------------------------------------
     Tabs (specs / compatibility). Without JS both panels show.
     WAI-ARIA tabs pattern: arrow keys, Home and End move between tabs.
     ------------------------------------------------------------------------ */
  class HelionTabs extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
      if (!this.list || this.tabs.length === 0) return;

      this.list.hidden = false;
      this.classList.add('is-ready');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
      const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(selected === -1 ? 0 : selected);
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (this.panels[i]) this.panels[i].hidden = !active;
      });
      if (focus) this.tabs[index].focus();
    }

    onKeydown(event, index) {
      const last = this.tabs.length - 1;
      const next = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[
        event.key
      ];
      if (next === undefined) return;
      event.preventDefault();
      this.select(next, true);
    }
  }

  if (!customElements.get('helion-tabs')) {
    customElements.define('helion-tabs', HelionTabs);
  }

  /* ------------------------------------------------------------------------
     Accordion (FAQ). Every answer shows without JS; with JS they start closed.
     ------------------------------------------------------------------------ */
  class HelionAccordion extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.helion-accordion__button'));
      this.buttons.forEach((button) => {
        this.setOpen(button, false);
        button.addEventListener('click', () => this.setOpen(button, button.getAttribute('aria-expanded') !== 'true'));
      });
      this.onBlockSelect = this.onBlockSelect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // Theme editor: open the question whose block is selected
    onBlockSelect(event) {
      const button = this.contains(event.target) && event.target.querySelector('.helion-accordion__button');
      if (button) this.setOpen(button, true);
    }

    setOpen(button, open) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      button.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
    }
  }

  if (!customElements.get('helion-accordion')) {
    customElements.define('helion-accordion', HelionAccordion);
  }

  /* ------------------------------------------------------------------------
     Product gallery: on phones it swipes; keep the "1 / 7" count in step.
     ------------------------------------------------------------------------ */
  class HelionGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.helion-gallery__track');
      this.current = this.querySelector('[data-gallery-current]');
      if (!this.track || !this.current) return;
      this.track.addEventListener(
        'scroll',
        () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            const width = this.track.clientWidth || 1;
            this.current.textContent = String(Math.round(this.track.scrollLeft / width) + 1);
          });
        },
        { passive: true }
      );
    }
  }

  if (!customElements.get('helion-gallery')) {
    customElements.define('helion-gallery', HelionGallery);
  }

  /* ------------------------------------------------------------------------
     Phones: bottom Add to bag bar, shown once the main button has scrolled
     up out of view. Its button submits the main form (form="…").
     ------------------------------------------------------------------------ */
  class HelionMobileAtc extends HTMLElement {
    connectedCallback() {
      const watched = document.querySelector(this.dataset.watch);
      if (!watched) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
        document.body.classList.toggle('has-mobile-atc', past);
      });
      this.observer.observe(watched);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.body.classList.remove('has-mobile-atc');
    }
  }

  if (!customElements.get('helion-mobile-atc')) {
    customElements.define('helion-mobile-atc', HelionMobileAtc);
  }

  /* ------------------------------------------------------------------------
     "Pairs well with": fetch Shopify's complementary products for this
     product. Show the row only if at least one comes back; otherwise the
     section stays hidden and is removed.
     ------------------------------------------------------------------------ */
  class HelionPairs extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      // A hidden element has no box to observe; watch its section wrapper instead.
      this.observer.observe(this.closest('.shopify-section') || this.parentElement);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      this.loaded = true;
      try {
        const response = await fetch(this.dataset.url);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('helion-pairs');
        if (fresh && fresh.querySelector('.helion-pairs__item')) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
          return;
        }
      } catch (error) {
        // Network trouble: leave the section hidden rather than half-built.
      }
      this.remove();
    }
  }

  if (!customElements.get('helion-pairs')) {
    customElements.define('helion-pairs', HelionPairs);
  }

  /* ------------------------------------------------------------------------
     First-view fade — hero and product images only ([data-helion-fade]).
     Without JS, or with reduced motion, images simply show.
     First-screen images ([data-helion-fade="load"]) fade in CSS from first paint.
     ------------------------------------------------------------------------ */
  let fadeObserver;

  function initFades(root = document) {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

    fadeObserver =
      fadeObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            requestAnimationFrame(() => entry.target.classList.remove('is-pending'));
            fadeObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.15 }
      );

    root.querySelectorAll('[data-helion-fade]:not([data-helion-fade="load"]):not(.is-faded)').forEach((element) => {
      element.classList.add('is-pending', 'is-faded');
      fadeObserver.observe(element);
    });
  }

  initFades();
  document.addEventListener('shopify:section:load', (event) => initFades(event.target));
})();
